import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import logo from '../../../../assets/img/logo.svg';
import ProgressBar from '../ProgressBar/ProgressBar';
import { TextButton } from '../Button/Button';
import {
    Page,
    Layout,
    Card,
    Header,
    TopBar,
    Brand,
    BrandTile,
    HeaderRight,
    LoginLink,
    DemoBadge,
    BackRow,
    Body,
    Content,
    Footer,
    DesktopPreview,
    MobilePreview,
    VisuallyHidden,
} from './OnboardingLayout.styles';

export const TOTAL_STEPS = 6;
/** Height of the sticky footer (CTA + secondary link + safe area) plus some air. */
const STICKY_FOOTER_SCROLL_PADDING = '160px';

/**
 * Shell of every signup v2 step: logo, demo badge, progress, optional "Volver", content and a
 * footer with the CTAs (sticky on mobile, inside the card on desktop).
 * When `onSubmit` is given, content + footer are wrapped in a `<form noValidate>`.
 */
const OnboardingLayout = ({
    step,
    total = TOTAL_STEPS,
    stepKey,
    title,
    headingId,
    isDemo = false,
    showLoginLink = false,
    onBack,
    backDisabled = false,
    onSubmit,
    footer,
    stickyFooter = true,
    liveMessage = '',
    preview = null,
    hideProgress = false,
    children,
}) => {
    const { t } = useTranslation();
    const isLast = step >= total;
    const progressLabel = isLast
        ? t('SignUpV2.progress.done')
        : t('SignUpV2.progress.label', { step, total });

    useEffect(() => {
        const previous = document.title;
        document.title = hideProgress
            ? `${title} | Ecometri`
            : t('SignUpV2.documentTitle', { progress: progressLabel, title });
        return () => {
            document.title = previous;
        };
    }, [progressLabel, title, t, hideProgress]);

    // Mobile sticky footer: keep focused fields and cards from scrolling underneath it.
    useEffect(() => {
        if (!stickyFooter) return undefined;
        const root = document.documentElement;
        const previous = root.style.scrollPaddingBottom;
        root.style.scrollPaddingBottom = STICKY_FOOTER_SCROLL_PADDING;
        return () => {
            root.style.scrollPaddingBottom = previous;
        };
    }, [stickyFooter]);

    const inner = (
        <>
            <Content key={stepKey}>{children}</Content>
            {preview && <MobilePreview>{preview}</MobilePreview>}
            {footer && <Footer sticky={stickyFooter}>{footer}</Footer>}
        </>
    );

    return (
        <Page>
            <Layout>
            <Card>
                <Header>
                    <TopBar>
                        <Brand>
                            <BrandTile>
                                <img src={logo} alt="" />
                            </BrandTile>
                            <span>{t('SignUpV2.brand')}</span>
                        </Brand>
                        {isDemo && (
                            <DemoBadge role="status">
                                <i className="ri-flask-line" aria-hidden="true" />
                                {t('SignUpV2.demo.badge')}
                            </DemoBadge>
                        )}
                        {showLoginLink && (
                            <HeaderRight>
                                <LoginLink to="/">
                                    {!isDemo && <span>{t('SignUpV2.header.haveAccount')}</span>}
                                    <strong>{t('SignUpV2.header.login')}</strong>
                                </LoginLink>
                            </HeaderRight>
                        )}
                    </TopBar>
                    {!hideProgress && (
                        <ProgressBar step={step} total={total} label={progressLabel} />
                    )}
                    {onBack && (
                        <BackRow>
                            <TextButton
                                icon="ri-arrow-left-line"
                                onClick={onBack}
                                disabled={backDisabled}
                            >
                                {t('SignUpV2.common.back')}
                            </TextButton>
                        </BackRow>
                    )}
                </Header>
                {onSubmit ? (
                    <Body as="form" noValidate onSubmit={onSubmit} aria-labelledby={headingId}>
                        {inner}
                    </Body>
                ) : (
                    <Body>{inner}</Body>
                )}
                <VisuallyHidden aria-live="polite" role="status">
                    {liveMessage}
                </VisuallyHidden>
            </Card>
            {preview && <DesktopPreview>{preview}</DesktopPreview>}
            </Layout>
        </Page>
    );
};

export default OnboardingLayout;
